import { Link, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Divider, Dropdown, Input, Modal, Radio, Space, Tooltip, message, Table, Typography, Drawer, Tag } from 'antd';
import { getInvitationGroupListApi, deleteInvitationGroupApi, getConferencesApi, saveInvitationGroupApi, updateInvitationGroupApi, invitationGroupMarkAsTesting } from '@services/Invitation/InvitationGroup';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import './InvitationGroupList.less';
import { TABLE_PAGE_SIZE_OPTIONS, drawerWidth } from '@utils/property';
import { invitationApplicationTypes } from '@utils/constants';
import { numberProject } from '@utils/utils';
import { invitationActions } from './store/invitationSlice';
// import { ExclamationCircleOutlined } from '@ant-design/icons';
import PageHeader from '@components/PageHeader/PageHeader';
import InvitationGroupFilterPanel from './InvitationGroupFilterPanel';

const { Text } = Typography;
const { Search } = Input;

const { setGroupFilter: setFilter, setGroupCursor: setCursor } = invitationActions;

/** 工具栏快速筛选：2026 医院管理大会关联医会议（正式邀请组） */
const QUICK_FILTER_HOSPITAL_MGMT_2026 = {
    conferenceId: 1497,
    applicationItemName: '擘画中国式现代化医院 第十四届健康界医院管理大会·2026',
    displayName: '2026医院管理大会',
};

export default function InvitationGroupList() {
    const navigate = useNavigate();

    const dispatch = useDispatch();
    const filter = useSelector(state => state.invitation.group.filter);
    const cursor = useSelector(state => state.invitation.group.cursor);
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const [total, setTotal] = useState(0);
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [conferencesPage, setConferencesPage] = useState(1);
    const [conferencesPageSize, setConferencesPageSize] = useState(5);
    const [conferencesTotal, setConferencesTotal] = useState(0);
    const [conferencesData, setConferencesData] = useState([]);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isModalVisibleTitle, setIsModalVisibleTitle] = useState('');
    const [invitationGroupName, setInvitationGroupName] = useState('');
    const [typeValue, setTypeValue] = useState(1);
    const [conferencesValue, setConferencesValue] = useState('');
    const [invitationGroupId, setInvitationGroupId] = useState(null);
    const [visibleViewUserDrawer, setVisibleViewUserDrawer] = useState(false);
    const closeViewUserDrawer = () => {
        setVisibleViewUserDrawer(false);
        setInvitationGroupName(null);
        setTypeValue(1);
        setSelectedRowKeys([]);
        setConferencesData([]);
        setConferencesTotal(0);
        setInvitationGroupId(null);
    };

    const onChangePage = (page, pageSize) => {
        setTableData([]);
        dispatch(
            setFilter({
                ...filter,
                page,
                pageSize,
            }),
        );
    };
    const onShowSizeChange = (_page, pageSize) => onChangePage(1, pageSize);

    const onFilterFinish = values => {
        dispatch(
            setFilter({
                ...filter,
                applicationItemName: values.applicationItemName,
                name: values.name,
                isTesting: values.isTesting,
                applicationType: 1,
                page: 1,
            }),
        );
    };

    const onFilterReset = () => {
        dispatch(
            setFilter({
                ...filter,
                applicationItemName: undefined,
                name: undefined,
                isTesting: undefined,
                applicationType: 1,
                page: 1,
            }),
        );
    };

    const news = () => {
        setVisibleViewUserDrawer(true);
    };

    const onInvitationGroupName = value => {
        setInvitationGroupName(value.target.value);
    };

    const onTypeChange = value => {
        setTypeValue(value.target.value);
    };

    const onConferencesSearch = value => {
        const params = {
            page: 1,
            pageSize: 5,
        };
        setConferencesValue(value);
        params.name = value;
        params.applicationType = typeValue;
        getConferencesApi(params).then(res => {
            if (res.status_code === 200) {
                setConferencesData(res.data.list);
                setConferencesTotal(res.data.totalCount);
            }
        });
    };
    const onChangeConferencesPage = (page, pageSize) => {
        setConferencesPage(page);
        setConferencesPageSize(pageSize);
        const params = {
            page: page,
            pageSize: pageSize,
        };
        params.name = conferencesValue;
        getConferencesApi(params).then(res => {
            if (res.status_code === 200) {
                setConferencesData(res.data.list);
                setConferencesTotal(res.data.totalCount);
            }
        });
    };
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const rowSelection = {
        type: 'radio',
        selectedRowKeys,
        onChange: keys => {
            setSelectedRowKeys(keys);
        },
    };

    const handleOk = () => {
        const params = {
            id: invitationGroupId,
            name: invitationGroupName,
        };

        updateInvitationGroupApi(params).then(res => {
            if (res.status_code === 200) {
                setInvitationGroupName(null);
                setTypeValue(1);
                setSelectedRowKeys([]);
                setConferencesData([]);
                setInvitationGroupId(null);
                setIsModalVisible(false);
                message.success(res.message);

                getInvitationGroups();
            }
        });
    };

    const okVisibleViewUserDrawer = () => {
        if (!invitationGroupName) {
            message.warning('请输入邀请组名称！');
            return;
        }

        if (selectedRowKeys[0] == null) {
            message.warning('请选择关联项目！');
            return;
        }

        const params = {
            applicationItemId: selectedRowKeys[0],
            applicationType: typeValue,
            hidden: 0,
            name: invitationGroupName,
        };
        saveInvitationGroupApi(params).then(res => {
            if (res.status_code === 200) {
                setInvitationGroupName(null);
                setTypeValue(1);
                setSelectedRowKeys([]);
                setConferencesData([]);
                setConferencesTotal(0);
                setVisibleViewUserDrawer(false);
                message.success(res.message);

                getInvitationGroups();
            }
        });
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setInvitationGroupName(null);
        setTypeValue(1);
        setSelectedRowKeys([]);
        setConferencesData([]);
        setInvitationGroupId(null);
    };

    const afterCloset = () => {
        setIsModalVisible(false);
        setInvitationGroupName(null);
        setTypeValue(1);
        setSelectedRowKeys([]);
        setConferencesData([]);
        setInvitationGroupId(null);
    };

    const closeVisibleViewUserDrawer = () => {
        setVisibleViewUserDrawer(false);
        setInvitationGroupName(null);
        setTypeValue(1);
        setSelectedRowKeys([]);
        setConferencesData([]);
        setConferencesTotal(0);
        setInvitationGroupId(null);
    };

    const onUpdateInvitation = item => {
        dispatch(setCursor(item.id));
        setIsModalVisible(true);
        setIsModalVisibleTitle('编辑邀请组');
        setInvitationGroupName(item.name);
        setInvitationGroupId(item.id);
    };

    const onDeleteInvitation = id => {
        Modal.confirm({
            title: '提示',
            content: '确定删除当前邀请组？',
            okText: '确认',
            cancelText: '取消',
            onOk: () => {
                deleteInvitationGroupApi({ id }).then(res => {
                    if (res.status_code === 200) {
                        message.success(res.message);
                    }

                    getInvitationGroups();
                });
            },
        });
    };

    const getInvitationGroups = async () => {
        try {
            setLoading(true);
            const params = {
                ...filter,
                limit: filter.pageSize,
            };
            const res = await getInvitationGroupListApi(params);
            if (res.status_code === 200) {
                setTableData(res.data.list);
                setTotal(res.data.totalCount);
            }
        } catch {
            //
        } finally {
            setLoading(false);
        }
    };

    const onRefresh = () => {
        void getInvitationGroups();
    };

    const hospitalMgmt2026ProjectNo = useMemo(() => numberProject('YHY', QUICK_FILTER_HOSPITAL_MGMT_2026.conferenceId), []);

    const isHospitalMgmt2026QuickFilterActive = useMemo(() => filter.applicationItemName === QUICK_FILTER_HOSPITAL_MGMT_2026.applicationItemName && filter.isTesting === 0 && !filter.name, [filter.applicationItemName, filter.isTesting, filter.name]);

    const applyHospitalMgmt2026QuickFilter = () => {
        dispatch(
            setFilter({
                ...filter,
                applicationItemName: QUICK_FILTER_HOSPITAL_MGMT_2026.applicationItemName,
                name: undefined,
                isTesting: 0,
                applicationType: 1,
                page: 1,
            }),
        );
    };

    const markAsTesting = record => {
        Modal.confirm({
            title: '标记为测试邀请组',
            icon: <i className="bi bi-exclamation-circle" />,
            content: '医疗决策人数据统计等数据指标将排除标记为测试的邀请组，确定标记为测试邀请组？',
            okText: '确认',
            cancelText: '取消',
            centered: true,
            onOk: () => {
                invitationGroupMarkAsTesting({
                    id: record.id,
                    isTesting: !!record.isTesting ? 0 : 1,
                }).then(res => {
                    if (res.status_code === 200) {
                        const msg = !!record.isTesting ? '标记为正式邀请组成功' : '标记为测试邀请组成功';
                        message.success(msg);

                        getInvitationGroups();
                    }
                });
            },
        });
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: 依赖 filter 语义即可
    useEffect(() => {
        getInvitationGroups();
    }, [filter]);

    const columns = [
        { title: '#', dataIndex: 'id', width: 60 },
        {
            title: '邀请组名称',
            dataIndex: 'name',
            width: 300,
            render: (_, record) => (
                <>
                    {record.name}
                    {!!record.isTesting && (
                        <Tag color="#f50" style={{ marginLeft: 4, padding: '0 4px' }}>
                            TEST
                        </Tag>
                    )}
                </>
            ),
        },
        {
            title: '关联项目',
            dataIndex: 'applicationItemName',
            render: (_, record) => (
                <>
                    {record.applicationItemName}
                    <br />
                    <small style={{ color: '#888' }}>
                        {invitationApplicationTypes.find(t => t.type === record.applicationType).name}项目：{numberProject('YHY', record.applicationItemId)}
                    </small>
                </>
            ),
        },
        {
            title: '邀请函数量',
            dataIndex: 'count',
            width: 120,
            render: (_, record) =>
                !!record.count ? (
                    <Tooltip title="点击进入邀请函管理">
                        <Link to={`/invitation/g/${record.id}/invitations`} style={{ marginRight: 8 }}>
                            {record.count}
                        </Link>
                    </Tooltip>
                ) : (
                    <Text type="secondary">暂无</Text>
                ),
        },
        {
            title: '操作',
            dataIndex: 'operation',
            width: 240,
            fixed: 'right',
            render: (_, record) => {
                const items = [
                    {
                        label: !!record.isTesting ? '标记为正式邀请组' : '标记为测试邀请组',
                        key: 'markAsTesting',
                        onClick: () => markAsTesting(record),
                    },

                    {
                        label: '删除邀请组',
                        key: 'delete',
                        onClick: () => onDeleteInvitation(record.id),
                        danger: true,
                    },
                ];

                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                dispatch(setCursor(record.id));
                                navigate(`/invitation/g/${record.id}/invitations`);
                            }}
                        >
                            管理邀请函
                        </Button>
                        <Button type="link" size="small" onClick={() => onUpdateInvitation(record)}>
                            编辑邀请组
                        </Button>
                        <Dropdown menu={{ items }} placement="bottomRight">
                            <Button size="small" type="link" className="more-operation">
                                <i className="bi bi-three-dots"></i>
                            </Button>
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];

    const conferencesColumns = [
        { title: '项目ID', dataIndex: 'id', width: 100 },
        { title: '项目名称', dataIndex: 'name' },
    ];

    useEffect(() => {
        document.title = '邀请组管理-医邀请';
    }, []);

    return (
        <div className="page-container list-page-container invitation-group-list-page">
            <PageHeader title="邀请组管理" breadcrumb={[{ label: '医邀请', path: '/invitation' }, { label: '邀请组管理' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <InvitationGroupFilterPanel filter={filter} onFinish={onFilterFinish} onReset={onFilterReset} />
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container invitation-group-list-table-region">
                <div className="main-toolbar">
                    <div className="main-tools">
                        <Space wrap size="middle">
                            <Button type="primary" onClick={news}>
                                新增邀请组
                            </Button>
                            <Space size="small" className="invitation-group-list-quick-filters">
                                <Tooltip title={`正式邀请组，医会议项目编号 ${hospitalMgmt2026ProjectNo}`}>
                                    <Button type={isHospitalMgmt2026QuickFilterActive ? 'primary' : 'default'} ghost={isHospitalMgmt2026QuickFilterActive} onClick={applyHospitalMgmt2026QuickFilter}>
                                        {QUICK_FILTER_HOSPITAL_MGMT_2026.displayName}
                                    </Button>
                                </Tooltip>
                            </Space>
                        </Space>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={onRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap invitation-group-list invitation-group-list-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        bordered
                        size="medium"
                        columns={columns}
                        dataSource={tableData}
                        loading={loading}
                        rowKey={item => item.id}
                        rowClassName={row => (row.id === cursor ? 'store-cursor' : '')}
                        scroll={{
                            x: 1100,
                            ...(tableScrollY != null ? { y: tableScrollY } : {}),
                        }}
                        sticky
                        pagination={{
                            total,
                            showTotal: () => `共${total}条`,
                            current: filter.page,
                            pageSize: filter.pageSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                            onChange: onChangePage,
                            onShowSizeChange,
                        }}
                    />
                </div>
            </div>

            <Modal
                title={isModalVisibleTitle}
                open={isModalVisible}
                destroyOnHidden={true}
                onOk={() => {
                    handleOk();
                }}
                onCancel={handleCancel}
                afterClose={afterCloset}
            >
                <div>
                    <span className="icon">邀请组：</span>
                    <Input style={{ width: 400 }} onChange={onInvitationGroupName} value={invitationGroupName} />
                </div>
            </Modal>

            <Drawer
                closable={{ placement: 'end' }}
                title="新增邀请组"
                size={drawerWidth}
                open={visibleViewUserDrawer}
                onClose={closeViewUserDrawer}
                footer={
                    <div className="custom-drawer-footer" style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={closeVisibleViewUserDrawer}>取消</Button>
                            <Button type="primary" onClick={okVisibleViewUserDrawer}>
                                确定
                            </Button>
                        </Space>
                    </div>
                }
            >
                <div>
                    <span className="icon">邀请组：</span>
                    <Input placeholder="输入邀请组名称" maxLength={100} showCount allowClear style={{ width: 520 }} onChange={onInvitationGroupName} value={invitationGroupName} />
                </div>
                <div className="postponeDiv">
                    <span className="postpone icon">项目类型：</span>
                    <Radio.Group onChange={onTypeChange} value={typeValue}>
                        <Radio value={1}>会议</Radio>
                    </Radio.Group>
                    <Search placeholder="关联项目" enterButton="搜索" onSearch={onConferencesSearch} />
                    <Table
                        className="enterprise-table"
                        columns={conferencesColumns}
                        dataSource={conferencesData}
                        rowKey={item => item.id}
                        rowSelection={rowSelection}
                        pagination={{
                            total: conferencesTotal,
                            showTotal: () => `共${conferencesTotal}条`,
                            current: conferencesPage,
                            pageSize: conferencesPageSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            onChange: onChangeConferencesPage,
                        }}
                    />
                </div>
            </Drawer>
        </div>
    );
}
